import { token } from '@trello/session-cookie';
import { sendNetworkErrorEvent } from '@trello/error-reporting';
import { parseNetworkError } from '@trello/graphql-error-handling';
import { getStringFromAdvancedDate } from '@trello/dashboard';
import { fetch } from '@trello/fetch';
import type {
  MutationCreateDashboardViewTileArgs,
  MutationUpdateDashboardViewTileArgs,
  MutationDeleteDashboardViewTileArgs,
  CreateDashboardViewTile,
  UpdateDashboardViewTile,
} from '../generated';
import { prepareDataForApolloCache } from '../prepareDataForApolloCache';
import type { TrelloRestResolver } from '../types';
import { getNetworkClient } from '../getNetworkClient';
import { isQueryInfo } from '../isQueryInfo';

const mapUpdateTileInputToPayload = (input: UpdateDashboardViewTile) => {
  return {
    ...(input.type && { type: input.type }),
    ...(input.graph && { graph_type: input.graph.type }),
    ...(input.from && {
      from: getStringFromAdvancedDate(input.from),
    }),
  };
};

const mapCreateTileInputToPayload = (input: CreateDashboardViewTile) => {
  return {
    type: input.type,
    graph_type: input.graph.type,
    pos: input.pos,
    ...(input.from && {
      from: getStringFromAdvancedDate(input.from),
    }),
  };
};

export const createDashboardViewTile: TrelloRestResolver<MutationCreateDashboardViewTileArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  if (!args.tile) {
    return;
  }
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/boards/${args.idBoard}/dashboardViewTiles`,
  );
  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      ...mapCreateTileInputToPayload(args.tile),
      token: context.token || token,
    }),
  });

  if (response.ok) {
    return prepareDataForApolloCache(await response.json(), rootNode);
  }

  sendNetworkErrorEvent({
    url: apiUrl,
    response: await response.clone().text(),
    status: response.status,
    operationName: context.operationName,
  });
  throw await parseNetworkError(response);
};

export const updateDashboardViewTile: TrelloRestResolver<MutationUpdateDashboardViewTileArgs> = async (
  obj,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  if (!args.tile) {
    return;
  }
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/boards/${args.idBoard}/dashboardViewTiles/${args.tile.id}`,
  );
  const response = await fetch(apiUrl, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      ...mapUpdateTileInputToPayload(args.tile),
      token: context.token || token,
    }),
  });

  if (response.ok) {
    return prepareDataForApolloCache(await response.json(), rootNode);
  }

  sendNetworkErrorEvent({
    url: apiUrl,
    response: await response.clone().text(),
    status: response.status,
    operationName: context.operationName,
  });
  throw await parseNetworkError(response);
};

export const deleteDashboardViewTile: TrelloRestResolver<MutationDeleteDashboardViewTileArgs> = async (
  obj,
  args,
  context,
) => {
  const networkClient = getNetworkClient();
  const response = await fetch(
    networkClient.getUrl(
      `/1/boards/${args.idBoard}/dashboardViewTiles/${args.idTile}`,
    ),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': context.clientAwareness.version,
      },
      body: JSON.stringify({
        token: context.token || token,
      }),
    },
  );

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error);
  }

  return true;
};
